import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  MessagesSquare, RefreshCw, Search, Flag, AlertTriangle, Filter,
  ChevronLeft, ChevronRight, ArrowUpRight,
} from "lucide-react";
import api from "../../lib/api";

/**
 * Brief 3.6 Phase A1 — Admin transcript reader (list view).
 *
 * Two modes via segmented control:
 *   • Threads  → `/api/admin/nerea/conversations`
 *   • Flagged  → `/api/admin/nerea/flags` (TWEAK 1 queue)
 *
 * Read-only here; the per-message flag/unflag surface lives on the
 * detail page.
 */

const CONFIDENCE_OPTIONS = [
  { key: "any",       label: "All confidence" },
  { key: "abstained", label: "Abstained only" },
  { key: "low",       label: "Low (no citations)" },
  { key: "medium",    label: "Medium (1-2 cites)" },
  { key: "high",      label: "High (3+ cites)" },
];

const CONFIDENCE_PILL = {
  high:      { dot: "bg-emerald-400", text: "text-emerald-300", label: "High" },
  medium:    { dot: "bg-amber-400",   text: "text-amber-300",   label: "Medium" },
  low:       { dot: "bg-rose-400",    text: "text-rose-300",    label: "Low" },
  abstained: { dot: "bg-zinc-500",    text: "text-zinc-400",    label: "Abstained" },
  none:      { dot: "bg-zinc-700",    text: "text-zinc-500",    label: "—" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function ConfidencePill({ value, count, testId }) {
  const cfg = CONFIDENCE_PILL[value] || CONFIDENCE_PILL.none;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[11px] ${cfg.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}{count != null ? ` · ${count}` : ""}
    </span>
  );
}

function ThreadsTable({ rows, loading }) {
  if (loading && !rows.length) {
    return (
      <div data-testid="conversations-loading" className="px-4 py-8 text-center text-[12.5px] text-zinc-500">
        Loading conversations…
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div data-testid="conversations-empty" className="px-4 py-10 text-center text-[12.5px] text-zinc-500">
        No conversations match those filters.
      </div>
    );
  }
  return (
    <table className="w-full text-[13px]" data-testid="conversations-table">
      <thead>
        <tr className="text-zinc-500 text-left text-[10.5px] uppercase tracking-[0.18em]">
          <th className="px-4 py-2.5">Merchant</th>
          <th className="px-4 py-2.5">Title</th>
          <th className="px-4 py-2.5">Last activity</th>
          <th className="px-4 py-2.5 text-right">Msgs</th>
          <th className="px-4 py-2.5 text-right">Abstain</th>
          <th className="px-4 py-2.5">Dominant confidence</th>
          <th className="px-4 py-2.5"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.thread_id}
            className="border-t border-white/[0.05] text-zinc-300 hover:bg-white/[0.03]"
            data-testid={`conversation-row-${r.thread_id}`}
          >
            <td className="px-4 py-2.5">
              <Link
                to={`/admin/merchants/${r.merchant_id}`}
                className="text-zinc-200 hover:text-[#00E5FF]"
                data-testid={`conversation-merchant-${r.thread_id}`}
              >
                {r.merchant_label || r.merchant_id || "—"}
              </Link>
            </td>
            <td className="px-4 py-2.5 text-zinc-300 max-w-[360px] truncate" title={r.title}>
              {r.title}
            </td>
            <td className="px-4 py-2.5 text-zinc-400" title={r.last_message_at || ""}>
              {fmtRelative(r.last_message_at) || "—"}
            </td>
            <td className="px-4 py-2.5 text-right text-zinc-400">{r.message_count}</td>
            <td className="px-4 py-2.5 text-right">
              {r.abstain_count > 0 ? (
                <span className="text-rose-300">{r.abstain_count}</span>
              ) : (
                <span className="text-zinc-600">0</span>
              )}
            </td>
            <td className="px-4 py-2.5">
              <ConfidencePill
                value={r.dominant_confidence}
                testId={`conversation-confidence-${r.thread_id}`}
              />
            </td>
            <td className="px-4 py-2.5 text-right">
              <Link
                to={`/admin/nerea/conversations/${r.thread_id}`}
                className="inline-flex items-center gap-1 text-[12px] text-[#00E5FF] hover:text-cyan-300"
                data-testid={`conversation-open-${r.thread_id}`}
              >
                Open <ArrowUpRight className="h-3 w-3" />
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FlagsTable({ rows, onResolve, loading, busyId }) {
  if (loading && !rows.length) {
    return (
      <div data-testid="flags-loading" className="px-4 py-8 text-center text-[12.5px] text-zinc-500">
        Loading flagged messages…
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div data-testid="flags-empty" className="px-4 py-10 text-center text-[12.5px] text-zinc-500">
        No flagged messages yet. Mark an assistant turn from a transcript to populate this queue.
      </div>
    );
  }
  return (
    <table className="w-full text-[13px]" data-testid="flags-table">
      <thead>
        <tr className="text-zinc-500 text-left text-[10.5px] uppercase tracking-[0.18em]">
          <th className="px-4 py-2.5">Flagged</th>
          <th className="px-4 py-2.5">Source</th>
          <th className="px-4 py-2.5">Merchant</th>
          <th className="px-4 py-2.5">Preview</th>
          <th className="px-4 py-2.5">Note</th>
          <th className="px-4 py-2.5"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr
            key={r.flag_id || `${r.thread_id}:${r.message_id}:${r.created_at || idx}`}
            className="border-t border-white/[0.05] text-zinc-300 align-top"
            data-testid={`flag-row-${r.flag_id || idx}`}
          >
            <td className="px-4 py-3 text-zinc-400 whitespace-nowrap" title={r.created_at}>
              {fmtRelative(r.created_at) || "—"}
            </td>
            <td className="px-4 py-3">
              <span className={`text-[11px] inline-flex items-center gap-1 ${r.source === "merchant" ? "text-amber-300" : "text-cyan-300"}`}>
                <Flag className="h-3 w-3" />
                {r.source === "merchant" ? "Merchant" : "Admin"}
              </span>
            </td>
            <td className="px-4 py-3 text-zinc-300">
              <Link
                to={`/admin/nerea/conversations/${r.thread_id}`}
                className="hover:text-[#00E5FF]"
                data-testid={`flag-open-thread-${r.flag_id}`}
              >
                {r.merchant_label || r.merchant_id || "—"}
              </Link>
            </td>
            <td className="px-4 py-3 text-zinc-400 max-w-[420px]">
              {r.message_preview ? (
                <div className="line-clamp-2">{r.message_preview}</div>
              ) : (
                <span className="text-zinc-600">— message no longer available —</span>
              )}
            </td>
            <td className="px-4 py-3 text-zinc-300 max-w-[280px]">
              {r.note ? <span>{r.note}</span> : <span className="text-zinc-600">—</span>}
            </td>
            <td className="px-4 py-3 text-right whitespace-nowrap">
              <Link
                to={`/admin/nerea/conversations/${r.thread_id}#msg-${r.message_id}`}
                className="text-[12px] text-[#00E5FF] hover:text-cyan-300 mr-3"
                data-testid={`flag-open-${r.flag_id}`}
              >
                Open
              </Link>
              <button
                onClick={() => onResolve(r.flag_id)}
                disabled={busyId === r.flag_id}
                className="text-[12px] text-zinc-400 hover:text-rose-300 disabled:opacity-50"
                data-testid={`flag-resolve-${r.flag_id}`}
              >
                Resolve
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AdminNereaConversations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "flags" ? "flags" : "threads";
  const [mode, setModeState] = useState(initialMode);

  // Toggle that also reflects the choice in the URL so refresh/share preserves it.
  function setMode(next) {
    setModeState(next);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    if (next === "flags") params.set("mode", "flags");
    else params.delete("mode");
    setSearchParams(params, { replace: true });
  }

  // Shared list state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const [charterMeta, setCharterMeta] = useState({ shipDate: null, version: null });

  // Threads filters
  const [search, setSearch] = useState("");
  const [confidence, setConfidence] = useState("any");
  const [abstainedOnly, setAbstainedOnly] = useState(false);
  const [busyFlagId, setBusyFlagId] = useState(null);

  // Reset page when filters change — handled inline in the setters
  // below to comply with React Compiler's set-state-in-effect rule.

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        if (mode === "threads") {
          const params = new URLSearchParams();
          params.set("page", String(page));
          params.set("page_size", String(pageSize));
          if (search.trim()) params.set("q", search.trim());
          if (confidence && confidence !== "any") params.set("confidence", confidence);
          if (abstainedOnly) params.set("abstained_only", "true");
          const { data } = await api.get(`/admin/nerea/conversations?${params.toString()}`);
          if (!cancelled) {
            setRows(data.rows || []);
            setTotal(data.total || 0);
            setCharterMeta({
              shipDate: data.charter_ship_date || null,
              version: data.current_charter_version || null,
            });
          }
        } else {
          const { data } = await api.get(
            `/admin/nerea/flags?page=${page}&page_size=${pageSize}`,
          );
          if (!cancelled) {
            setRows(data.rows || []);
            setTotal(data.total || 0);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, page, pageSize, reloadTick, confidence, abstainedOnly, search]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize],
  );

  // Brief 3.6 Phase D Part 2 — pre-charter banner gate. Renders when the
  // current page contains rows whose last_message_at is older than the
  // charter ship date. Banner explains the rows reflect older behavior
  // rules, not the current charter.
  const hasPreCharterRows = useMemo(() => {
    if (mode !== "threads" || !charterMeta.shipDate || rows.length === 0) {
      return false;
    }
    const ship = new Date(charterMeta.shipDate).getTime();
    return rows.some((r) => {
      const t = r.last_message_at ? new Date(r.last_message_at).getTime() : 0;
      return t > 0 && t < ship;
    });
  }, [mode, charterMeta.shipDate, rows]);

  function fmtShipDate(iso) {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    } catch {
      return iso;
    }
  }

  async function handleResolveFlag(flagId) {
    setBusyFlagId(flagId);
    try {
      await api.delete(`/admin/nerea/conversations/flags/${flagId}`);
      setRows((prev) => prev.filter((r) => r.flag_id !== flagId));
      setTotal((n) => Math.max(0, n - 1));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to resolve flag.");
    } finally {
      setBusyFlagId(null);
    }
  }

  return (
    <div className="max-w-6xl" data-testid="admin-nerea-conversations">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <MessagesSquare className="h-3 w-3 text-[#00E5FF]" /> Nerea
      </div>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
        Conversations
      </h1>
      <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-2xl">
        Read every merchant ↔ Nerea exchange. Filter by confidence to spot
        content gaps, jump into a transcript, and mark any assistant turn
        for review. Source:
        <code className="ml-1 text-zinc-500">merchant_chat_threads</code>.
      </p>

      {/* Mode segmented control */}
      <div className="mt-6 flex items-center gap-2" data-testid="conversations-mode-switch">
        <button
          data-testid="mode-threads"
          onClick={() => setMode("threads")}
          className={`text-[12px] px-3 py-1.5 rounded-lg border transition-colors ${
            mode === "threads"
              ? "bg-[#00E5FF]/10 border-[#00E5FF]/30 text-[#00E5FF]"
              : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
          }`}
        >
          Threads
        </button>
        <button
          data-testid="mode-flags"
          onClick={() => setMode("flags")}
          className={`text-[12px] px-3 py-1.5 rounded-lg border transition-colors inline-flex items-center gap-1.5 ${
            mode === "flags"
              ? "bg-amber-400/10 border-amber-400/30 text-amber-300"
              : "bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05]"
          }`}
        >
          <Flag className="h-3 w-3" /> Flagged messages
        </button>
        <button
          data-testid="conversations-reload"
          onClick={() => setReloadTick((t) => t + 1)}
          className="ml-auto inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border bg-white/[0.02] border-white/[0.08] text-zinc-400 hover:bg-white/[0.05] transition-colors"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Reload
        </button>
      </div>

      {/* Threads filter bar */}
      {mode === "threads" && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="conversations-filters">
          <div className="inline-flex items-center gap-2 bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-1.5">
            <Search className="h-3.5 w-3.5 text-zinc-500" />
            <input
              data-testid="conversations-search"
              type="text"
              placeholder="Search merchant or title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent text-[12.5px] text-zinc-300 placeholder-zinc-600 outline-none w-56"
            />
          </div>
          <div className="inline-flex items-center gap-2 bg-white/[0.02] border border-white/[0.08] rounded-lg px-3 py-1.5">
            <Filter className="h-3.5 w-3.5 text-zinc-500" />
            <select
              data-testid="conversations-confidence-filter"
              value={confidence}
              onChange={(e) => { setConfidence(e.target.value); setPage(1); }}
              className="bg-transparent text-[12.5px] text-zinc-300 outline-none"
            >
              {CONFIDENCE_OPTIONS.map((o) => (
                <option key={o.key} value={o.key} className="bg-zinc-900">
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer">
            <input
              data-testid="conversations-abstained-toggle"
              type="checkbox"
              checked={abstainedOnly}
              onChange={(e) => { setAbstainedOnly(e.target.checked); setPage(1); }}
              className="accent-rose-400"
            />
            <AlertTriangle className="h-3 w-3 text-rose-300" />
            Has abstentions
          </label>
        </div>
      )}

      {err && (
        <div data-testid="conversations-error" className="mt-4 rounded-lg border border-rose-400/30 bg-rose-500/[0.05] px-3 py-2 text-[12.5px] text-rose-200">
          {err}
        </div>
      )}

      {hasPreCharterRows && (
        <div
          data-testid="pre-charter-banner"
          className="mt-4 rounded-lg border border-amber-400/30 bg-amber-400/[0.05] px-3 py-2 text-[12.5px] text-amber-200 flex items-start gap-2"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Conversations dated before {fmtShipDate(charterMeta.shipDate)} predate Charter {charterMeta.version}.
            Nerea&apos;s behavior in those threads may differ from current rules.
          </span>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
        {mode === "threads" ? (
          <ThreadsTable rows={rows} loading={loading} />
        ) : (
          <FlagsTable
            rows={rows}
            onResolve={handleResolveFlag}
            loading={loading}
            busyId={busyFlagId}
          />
        )}
      </div>

      {/* Pagination footer */}
      <div className="mt-4 flex items-center justify-between text-[12px] text-zinc-500" data-testid="conversations-pagination">
        <div>
          {total > 0 ? (
            <>
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </>
          ) : (
            <>No results</>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="conversations-page-prev"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.08] text-zinc-400 hover:bg-white/[0.05] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </button>
          <span data-testid="conversations-page-indicator">
            Page {page} / {totalPages}
          </span>
          <button
            data-testid="conversations-page-next"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.08] text-zinc-400 hover:bg-white/[0.05] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Hidden but useful — last updated indicator for keyboard-driven QA. */}
      <div className="mt-2 text-[11px] text-zinc-600" data-testid="conversations-now">
        {fmtDateTime(new Date().toISOString())}
      </div>
    </div>
  );
}
